import * as THREE from "three";
import {CustomMaterials} from "./CustomMaterials";
import Engine from './Engine';
import Utils from './Utilities';
import WorkspaceManager from './WorkspaceManager';
import QuizManager from "./QuizManager";
import Scenario from './scenarioElements/Scenario';
import PartsCatalogUtils from './utilities/PartsCatalogUtils';
import OutlineManager from "./OutlineManager";
import { areArrayEqualUnordered } from "./utilities/Misc";
import { assert, ErrorCategory } from "./utilities/ErrorHandling";
import TroikaText3D from "./Text3D";
import ReduxStore from '../state/store';
import { CustomEventTypes, ChecklistType} from '../globals';

interface WindowMovedEvent extends CustomEvent {
	detail: {
		windowId: string;
	};
}

export default class PostProcessManager
{
	postProcessingScene: THREE.Scene;
	fullScreenTriangle: THREE.Mesh<THREE.BufferGeometry<THREE.NormalBufferAttributes>, THREE.Material | THREE.Material[], THREE.Object3DEventMap>;
	renderTarget: THREE.WebGLRenderTarget<THREE.Texture>;
	selectionOutlineRenderTargets: Map<string, THREE.WebGLRenderTarget<THREE.Texture>>;
	selectionOutlineDirtyFlags: Map<string, boolean>;
	outlinePostprocessMaterial: THREE.RawShaderMaterial;
	plainTextureMaterial: THREE.RawShaderMaterial;
	outlineManager: OutlineManager = new OutlineManager();
	constructor()
	{
		const triangleGeometry = new THREE.BufferGeometry();
		triangleGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1.0, -1.0, 0.0, 3.0, -1.0, 0.0, -1.0, 3.0, 0.0,]), 3));
		
		this.postProcessingScene = new THREE.Scene();
		this.fullScreenTriangle = new THREE.Mesh(triangleGeometry);
		this.fullScreenTriangle.frustumCulled = false;

		this.renderTarget = new THREE.WebGLRenderTarget(0, 0, { generateMipmaps: false, format: THREE.RGBAFormat, stencilBuffer: false, depthBuffer: false });
		this.selectionOutlineRenderTargets = new Map();
		this.selectionOutlineDirtyFlags = new Map();

		this.outlinePostprocessMaterial = new THREE.RawShaderMaterial({
			transparent: true,
			depthWrite: false,
			depthTest: false,
			vertexShader: basePostprocessVertexShader,
			fragmentShader: outlinePostprocessFragmentShader,
			uniforms: {
				u_texture: { value: this.renderTarget.texture },
				u_resolution: { value: new THREE.Vector2() },
				u_outlineColor: { value: new THREE.Color() },
				u_fill: { value: false },
			},
		  });

		this.plainTextureMaterial = new THREE.RawShaderMaterial({
			transparent: true,
			depthWrite: false,
			depthTest: false,
			vertexShader: basePostprocessVertexShader,
			fragmentShader: plainTextureFragmentShader,
			uniforms: {
				u_texture: { value: null },
				u_resolution: { value: new THREE.Vector2() },
			},
		  });

		this.setOutlineRenderDirty = this.setOutlineRenderDirty.bind(this);
		document.addEventListener(CustomEventTypes.objectSelectionChanged, () => this.invalidateOutlineCache());
		document.addEventListener(CustomEventTypes.selectedObjectPositionChanged, () => this.invalidateOutlineCache());
		document.addEventListener(CustomEventTypes.selectedObjectRotationChanged, () => this.invalidateOutlineCache());
		document.addEventListener(CustomEventTypes.selectedObjectScaleChanged, () => this.invalidateOutlineCache());
		document.addEventListener(CustomEventTypes.selectedObjectAnimated, () => this.invalidateOutlineCache());

		document.addEventListener(CustomEventTypes.windowMoved, (e: Event) =>
		{
			const event = e as WindowMovedEvent;
			if (this.selectionOutlineDirtyFlags.has(event.detail.windowId))
			{
				this.cachedObjects = [];
				this.setOutlineRenderDirty(true, event.detail.windowId);
			}
		});
	}

	invalidateOutlineCache()
	{
		this.cachedObjects = [];
		this.setOutlineRenderDirty()
	}

	render(sceneViewId: string)
	{
		if(!Utils.IsSimulationRunning())
		{
			this.renderSelectionOutlines(sceneViewId);
		}
		else
		{
			this.renderQuizHighlights(sceneViewId);
			this.renderPartsCatalogueHighlights(sceneViewId);
		}
	}	

	renderQuizHighlights(sceneViewId: string)
	{
		const question = QuizManager.getQuestion();
		if(!question || (question.type !== ChecklistType.MULTI_OPTION_3D && question.type !== ChecklistType.MULTI_OPTION_SELECT_3D)) return;

		const selectedObjects: THREE.Object3D[] = []; 
		const answers = ReduxStore.getState().checklistModule.answers;
		const currentStep = Scenario.currentStep();
		if (!currentStep) return;
		
		const stepId = currentStep.id;
		if(answers[stepId])
		{
			for(let i = 0; i < answers[stepId].length; i++)
			{
				const answer = answers[stepId][i];
				const object = QuizManager.getQuizSceneObject(question.options[i].object);
		
				if(object && answer)
				{
					selectedObjects.push(object);
				}
			}
			
			if(selectedObjects.length && question.highlightColor) 
			{
				this.renderOutline(sceneViewId, selectedObjects, "0x"+question.highlightColor.substring(1), false);
			}
		}
	}

	renderPartsCatalogueHighlights(sceneViewId: string)
	{
		const currentStep = Scenario.currentStep();
		if (!currentStep) return;

		if(ReduxStore.getState().partsCatalogModule.selectedPart[currentStep.id]) return;
		
		const highlightedObjectIds = ReduxStore.getState().partsCatalogModule.simulationHighlightedObjects[currentStep.id] ?? [];
		const highlightedObjects: THREE.Object3D[] = highlightedObjectIds
			.map(objectId => PartsCatalogUtils.GetPartsCatalogObject(objectId))
			.filter((object): object is THREE.Object3D => object !== null);

		const temporaryHighlightedPartId = ReduxStore.getState().partsCatalogModule.highlightedPartId;
		if(temporaryHighlightedPartId && !highlightedObjectIds.includes(temporaryHighlightedPartId))
		{
			const object = PartsCatalogUtils.GetPartsCatalogObject(temporaryHighlightedPartId);
			if (object) highlightedObjects.push(object);
		} 
		const hoveredPartId = ReduxStore.getState().partsCatalogModule.hoveredPartId;
		if(hoveredPartId && !highlightedObjectIds.includes(hoveredPartId))
		{
			const object = PartsCatalogUtils.GetPartsCatalogObject(hoveredPartId);
			if (object) highlightedObjects.push(object);
		}

		if(!highlightedObjects.length) return;

		const catalog = ReduxStore.getState().partsCatalogModule.catalogs[currentStep.id];
		if (!catalog?.highlightColor) return;

		this.renderOutline(sceneViewId, highlightedObjects, "0x"+catalog.highlightColor.substring(1), false);
	}

	renderSelectionOutlines(sceneViewId: string)
	{
		const selection = Engine.getInstance().getPickingManager().getCurrentSelection();
		if(!selection.objects.length) return;
		
		const outputRenderTarget = this.getSelectionOutlineRenderTarget(sceneViewId);
		const dirty = this.getSelectionOutlineDirtyFlag(sceneViewId);

		if (dirty)
		{
			this.renderOutline(sceneViewId, selection.objects, selection.groupGuid ? 0xF77607 : 0xEEA904, false, outputRenderTarget);
			this.setOutlineRenderDirty(false, sceneViewId);
		}

		const renderer = Engine.getInstance().getRenderer();
		const sceneViewData = WorkspaceManager.getSceneViewData(sceneViewId);
		if (!sceneViewData) return;

		const boundingRect = sceneViewData.context.canvas.getBoundingClientRect();
		this.plainTextureMaterial.uniforms["u_texture"].value = outputRenderTarget.texture;
		this.plainTextureMaterial.uniforms["u_resolution"].value.set(boundingRect.width, boundingRect.height);
		this.fullScreenTriangle.material = this.plainTextureMaterial;
		renderer.setRenderTarget( null );
		renderer.render(this.fullScreenTriangle, sceneViewData.activeCamera);
	}

	private cachedObjects: THREE.Object3D[] = [];
	private outlineObjects: THREE.Mesh[] = [];

	renderOutline(sceneViewId: string, objects: THREE.Object3D[], colorHex: string | number, fill = false, outputRenderTarget: THREE.WebGLRenderTarget<THREE.Texture> | null = null)
	{
		const renderer = Engine.getInstance().getRenderer();
		const sceneViewData = WorkspaceManager.getSceneViewData(sceneViewId);
		if (!sceneViewData) return;

		const boundingRect = sceneViewData.context.canvas.getBoundingClientRect();
		
		//sprites (callouts, lights, cameras etc) and lines (markerless ar tracker, light helpers etc) need to be hidden
		//everything else will be rendered plain white due to postProcessingScene.overrideMaterial
		const hiddenObjects: THREE.Object3D[] = [];
		for (const object of objects)
		{
			object.traverse(function(child: THREE.Object3D)
			{
				const isSprite = (child as any).isSprite;
				const material = (child as any).material;
				if (child.visible && !child.userData.requiresOutline &&
						(child.userData.hideInHierarchy || isSprite || (material && material instanceof THREE.LineBasicMaterial)))
				{
					child.visible = false;
					hiddenObjects.push(child);
				}
			});
		}

		this.renderTarget.setSize(boundingRect.width, boundingRect.height);
		if(outputRenderTarget)
		{
			outputRenderTarget.setSize(boundingRect.width, boundingRect.height);
			renderer.setRenderTarget( outputRenderTarget );
			renderer.clear();
		}

		this.postProcessingScene.overrideMaterial = CustomMaterials.getPlainWhiteMat();
		this.outlinePostprocessMaterial.uniforms["u_outlineColor"].value.setHex(colorHex, THREE.LinearSRGBColorSpace);
		this.outlinePostprocessMaterial.uniforms["u_fill"].value = fill;
		this.outlinePostprocessMaterial.uniforms["u_resolution"].value.set(boundingRect.width, boundingRect.height);
		
		//one by one, pass each selected object to the render scene and render it to the render target, apply a post process and render to screen
		//this results in a complete outline for each individually selected object
		if (!areArrayEqualUnordered(this.cachedObjects, objects))
		{
			this.cachedObjects = objects;
			this.outlineObjects = this.outlineManager.processObjects(objects);
		}
		for (let i = 0; i < this.outlineObjects.length; i++)
		{
			const object = this.outlineObjects[i];
			if (!object) continue;
			if (!object.visible) continue;
			this.postProcessingScene.overrideMaterial = object instanceof TroikaText3D ? null : CustomMaterials.getPlainWhiteMat();
			if (object instanceof TroikaText3D)
			{
				object.troikaText.color = "#ffffff";
				object.troikaText.fillOpacity = 1;
				// object.troikaText.colorRanges = null;
			}

			this.postProcessingScene.children.push(object); //directly pushing objects as children as opposed to using "attach", this prevents useless matrix updats from happening
			const mesh = object as THREE.Mesh;
			if (!fill && mesh.geometry && object.children.length && mesh.material)
			{
				const isMaterialVisible = Array.isArray(mesh.material) 
					? mesh.material.some(m => m.visible)
					: mesh.material.visible;

				if (isMaterialVisible)
				{
					//first render the object without any of its children, so it recieves its own full outline
					const children = [...object.children];
					object.children.length = 0;
					renderer.setRenderTarget( this.renderTarget );
					renderer.clear();
					renderer.render(this.postProcessingScene, sceneViewData.activeCamera);

					this.fullScreenTriangle.material = this.outlinePostprocessMaterial;
					renderer.setRenderTarget( outputRenderTarget );
					renderer.render(this.fullScreenTriangle, sceneViewData.activeCamera);
					object.children = children;
				}
			}

			renderer.setRenderTarget( this.renderTarget );
			renderer.clear();
			renderer.render(this.postProcessingScene, sceneViewData.activeCamera);

			this.fullScreenTriangle.material = this.outlinePostprocessMaterial;
			renderer.setRenderTarget( outputRenderTarget );
			renderer.render(this.fullScreenTriangle, sceneViewData.activeCamera);
			this.postProcessingScene.children.pop();
			if (object instanceof TroikaText3D)
			{
				object.setTextColor(object.getTextColor());
				object.troikaText.fillOpacity = object.fadeOpacity;
				// object.troikaText.colorRanges = object.colorRange;
			}
		}

		hiddenObjects.forEach(function(object) {object.visible = true;});
	}

	getSelectionOutlineRenderTarget(sceneViewId: string): THREE.WebGLRenderTarget<THREE.Texture>
	{
		if (!this.selectionOutlineRenderTargets.has(sceneViewId))
		{
			const sceneViewData = WorkspaceManager.getSceneViewData(sceneViewId);
			assert(sceneViewData, `No scene view data found for sceneViewId: ${sceneViewId}`, ErrorCategory.General);
			const boundingRect = sceneViewData!.context.canvas.getBoundingClientRect();
			this.selectionOutlineRenderTargets.set(sceneViewId, new THREE.WebGLRenderTarget(boundingRect.width, boundingRect.height,
				{ generateMipmaps: false, format: THREE.RGBAFormat, stencilBuffer: false, depthBuffer: false }));
		}

		return this.selectionOutlineRenderTargets.get(sceneViewId)!;
	}

	getSelectionOutlineDirtyFlag(sceneViewId: string): boolean
	{
		if (!this.selectionOutlineDirtyFlags.has(sceneViewId))
		{
			this.selectionOutlineDirtyFlags.set(sceneViewId, true);
		}

		return this.selectionOutlineDirtyFlags.get(sceneViewId)!;
	}

	setOutlineRenderDirty(dirty = true, sceneViewId = "")
	{
		if (sceneViewId)
		{
			this.selectionOutlineDirtyFlags.set(sceneViewId, dirty);
		}
		else
		{
			for (const sceneViewId of this.selectionOutlineDirtyFlags.keys())
			{
				this.selectionOutlineDirtyFlags.set(sceneViewId, dirty);
			}
		}
	}
}

const basePostprocessVertexShader = 
`precision highp float;
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 1.0, 1.0);
}`;

//http://www.geoffprewett.com/blog/software/opengl-outline/index.html
const outlinePostprocessFragmentShader = 
`precision highp float;
uniform sampler2D u_texture;
uniform vec2 u_resolution;
uniform vec3 u_outlineColor;
uniform bool u_fill;

const int WIDTH = 2;

void main() {
 	vec2 uv = gl_FragCoord.xy / u_resolution.xy;

   // bool isInside = false;
    int count = 0;
    float coverage = 0.0;
    float nearestDistance = 1e6;

	//sample all nearby pixels based on desired line width
    for (int y = -WIDTH;  y <= WIDTH;  ++y)
	{
        for (int x = -WIDTH;  x <= WIDTH;  ++x)
		{
            vec2 dUV = vec2(float(x) / u_resolution.x, float(y) / u_resolution.y);
            float mask = texture2D(u_texture, uv + dUV).r; //sample nearby pixel, will always return 0 (black) or 1 (white, meaning the pixel contains a rendered objects).
            coverage += mask; //accumulate the number of white pixels
            if (mask > 0.0) nearestDistance = min(nearestDistance, sqrt(float(x * x + y * y)));
            //if (x == 0 && y == 0) isInside = (mask > 0.5);
            count++;
        }
    }
    coverage /= float(count); //coverage now represents the percentage [0-1] of nearby white pixels
	//coverage of 1.0 means all nearby pixels are white, hence this pixel is not part of the outline
	//coverage of 0.0 means no nearby pixels are white, hence this pixel is too far away to be part of the outline

	if(u_fill && coverage > 0.9)
	{
		gl_FragColor = vec4(u_outlineColor, 0.5);
	}
	else
	{
		if(coverage < 0.1 || coverage > 0.9) discard;
		gl_FragColor = vec4(u_outlineColor, 1.0);
	}
	

	//float a;
    // if (isInside)
	// {
    //     a = min(1.0, (1.0 - coverage) / 0.15);
    // }
	// else
	// {
    //     const float solid = 0.3 * float(WIDTH);
    //     const float fuzzy = float(WIDTH) - solid;
    //     a = 1.0 - min(1.0, max(0.0, nearestDistance - solid) / fuzzy);
    // }
	// gl_FragColor = vec4(u_outlineColor, a);
}`;


const plainTextureFragmentShader = 
`precision highp float;
uniform sampler2D u_texture;
uniform vec2 u_resolution;
void main() {
 	vec2 uv = gl_FragCoord.xy / u_resolution.xy;
	gl_FragColor = texture2D(u_texture, uv);
}`;
